import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { normalizeStructuredHours } from "@/lib/businessHours";
import { BusinessProfileSection } from "@/components/org/settings/BusinessProfileSection";
import { OrgBrandingSection } from "@/components/org/settings/OrgBrandingSection";
import { LocationSection } from "@/components/org/settings/LocationSection";
import { LocationPreview } from "@/components/org/settings/LocationPreview";
import { OperationsSection } from "@/components/org/settings/OperationsSection";
import { BusinessLocationsPanel } from "@/components/org/settings/BusinessLocationsPanel";
import { PrimaryContactSection } from "@/components/org/settings/PrimaryContactSection";
import { CustomerFacingInfoSection } from "@/components/org/settings/CustomerFacingInfoSection";
import { RegionalSettingsSection } from "@/components/org/settings/RegionalSettingsSection";
import { SessionSecuritySection } from "@/components/org/settings/SessionSecuritySection";
import { SettingsSaveBar } from "@/components/org/settings/SettingsSaveBar";

const normalizeAddress = (addr = {}) => ({
  street: addr.street || "",
  line2: addr.line2 || "",
  city: addr.city || "",
  state: addr.state || "",
  zip: addr.zip || "",
  country: addr.country || "US",
  lat: addr.lat ?? null,
  lng: addr.lng ?? null,
  verified: addr.verified || false,
  verified_at: addr.verified_at || null,
  verified_by_user_id: addr.verified_by_user_id || null,
  location_source: addr.location_source || null,
});

const PHYSICAL_ADDRESS_FIELDS = ["street", "city", "state", "zip", "country"];

const buildFormFromOrg = (org) => ({
  name: org.name || "",
  business_phone: org.business_phone || "",
  business_email: org.business_email || "",
  website: org.website || "",
  description: (org.description || "").slice(0, 250),
  business_ein: org.business_ein || "",
  address: normalizeAddress(org.address),
  timezone: org.timezone || "",
  business_hours_structured: normalizeStructuredHours(org.business_hours_structured),
  primary_contact_user_id: org.primary_contact_user_id || "",
  primary_contact: org.primary_contact || "",
  primary_contact_title: org.primary_contact_title || "",
  primary_contact_email: org.primary_contact_email || "",
  primary_contact_phone: org.primary_contact_phone || "",
  customer_facing_name: org.customer_facing_name || "",
  customer_service_phone: org.customer_service_phone || "",
  customer_service_email: org.customer_service_email || "",
  customer_facing_website: org.customer_facing_website || "",
  date_format: org.date_format || "",
  time_format: org.time_format || "",
  currency: org.currency || "",
  inactivity_timeout_minutes: org.inactivity_timeout_minutes ?? null,
});

export default function OrgSettings() {
  const { API } = useAuth();
  const [org, setOrg] = useState(null);
  const [form, setForm] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyStatus, setVerifyStatus] = useState(null);
  const [platformDefaultInactivityMinutes, setPlatformDefaultInactivityMinutes] = useState(30);
  const successTimer = useRef(null);

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await axios.get(`${API}/org/settings`, { withCredentials: true });
      setOrg(data.organization);
      setForm(buildFormFromOrg(data.organization));
      setLogoUrl(data.organization.logo_url || null);
      setPlatformDefaultInactivityMinutes(data.platform_default_inactivity_minutes ?? 30);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const dirty = org && form && JSON.stringify(form) !== JSON.stringify(buildFormFromOrg(org));

  const patchField = (patch) => setForm((f) => ({ ...f, ...patch }));
  const patchAddress = (patch) => setForm((f) => {
    const nextAddress = { ...f.address, ...patch };
    const touchesPhysical = Object.keys(patch).some((k) => PHYSICAL_ADDRESS_FIELDS.includes(k));
    if (touchesPhysical && !("verified" in patch)) {
      nextAddress.verified = false;
      nextAddress.location_source = null;
    }
    return { ...f, address: nextAddress };
  });

  const handleVerify = async () => {
    setVerifying(true);
    setVerifyStatus(null);
    try {
      const { data } = await axios.post(`${API}/org/settings/verify-address`, {
        street: form.address.street, city: form.address.city,
        state: form.address.state, zip: form.address.zip, country: form.address.country,
      }, { withCredentials: true });
      setVerifyStatus(data);
      if (data.verified && data.address) {
        setForm((f) => ({
          ...f,
          address: {
            ...f.address,
            lat: data.address.lat, lng: data.address.lng, verified: data.address.verified,
            verified_at: data.address.verified_at, verified_by_user_id: data.address.verified_by_user_id,
            location_source: data.address.location_source,
          },
        }));
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setVerifying(false);
    }
  };

  const handleIdentifyConfirm = async (lat, lng) => {
    try {
      const { data } = await axios.post(`${API}/org/settings/identify-location`, { lat, lng }, { withCredentials: true });
      const normalized = normalizeAddress(data.address);
      setOrg((o) => ({ ...o, address: normalized }));
      setForm((f) => ({ ...f, address: normalized }));
      setVerifyStatus(null);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError("");
    if (form.inactivity_timeout_minutes != null) {
      const n = Number(form.inactivity_timeout_minutes);
      if (Number.isNaN(n) || n < 30 || n > 100) {
        setError("Session inactivity timeout must be between 30 and 100 minutes.");
        setSaving(false);
        return;
      }
    }
    try {
      const { data } = await axios.patch(`${API}/org/settings`, {
        name: form.name, business_phone: form.business_phone, business_email: form.business_email || null,
        website: form.website, description: form.description, business_ein: form.business_ein,
        address: form.address, timezone: form.timezone, business_hours_structured: form.business_hours_structured,
        primary_contact_user_id: form.primary_contact_user_id, primary_contact: form.primary_contact,
        primary_contact_title: form.primary_contact_title, primary_contact_email: form.primary_contact_email || null,
        primary_contact_phone: form.primary_contact_phone,
        customer_facing_name: form.customer_facing_name, customer_service_phone: form.customer_service_phone,
        customer_service_email: form.customer_service_email || null, customer_facing_website: form.customer_facing_website,
        date_format: form.date_format, time_format: form.time_format, currency: form.currency,
        inactivity_timeout_minutes: form.inactivity_timeout_minutes,
      }, { withCredentials: true });
      setOrg(data.organization);
      setForm(buildFormFromOrg(data.organization));
      setSuccess("Organization settings saved.");
      clearTimeout(successTimer.current);
      successTimer.current = setTimeout(() => setSuccess(""), 4000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setForm(buildFormFromOrg(org));
    setVerifyStatus(null);
  };

  if (!form) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400" data-testid="org-settings-loading">
        {error || "Loading organization settings…"}
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-6" data-testid="org-settings-page">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-950 dark:text-white">Organization Settings</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Manage your organization's business profile, branding and preferences.</p>
      </div>

      {error && <p className="rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="org-settings-error">{error}</p>}
      {success && <p className="rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400" data-testid="org-settings-success">{success}</p>}

      <div className="grid gap-6 lg:grid-cols-[3fr_2fr]">
        <BusinessProfileSection form={form} legalName={org.legal_name} onChange={patchField} />
        <OrgBrandingSection logoUrl={logoUrl} onLogoChange={setLogoUrl} />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <LocationSection
          form={form}
          onAddressChange={patchAddress}
          onVerify={handleVerify}
          verifying={verifying}
          verifyStatus={verifyStatus}
        />
        <BusinessLocationsPanel />
        <LocationPreview address={form.address} editable onIdentifyConfirm={handleIdentifyConfirm} fill />
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        <OperationsSection form={form} />
        <PrimaryContactSection form={form} onChange={patchField} />
        <CustomerFacingInfoSection form={form} onChange={patchField} />
        <RegionalSettingsSection form={form} onChange={patchField} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <SessionSecuritySection form={form} onChange={patchField} platformDefault={platformDefaultInactivityMinutes} />
      </div>

      <SettingsSaveBar dirty={!!dirty} saving={saving} onSave={handleSave} onDiscard={handleDiscard} />
    </div>
  );
}
